import config from '../config/config';
import {
    ApolloClient as ApolloClientSource,
    useApolloClient as useApolloClientSource,
    ApolloError,
    ApolloLink,
    createHttpLink,
    InMemoryCache,
    StoreObject,
    Observable,
    ServerParseError,
    ServerError
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import ApolloLinkTimeout from 'apollo-link-timeout';
import { Store } from 'redux';
import { print } from 'graphql';

import IStore from './store.interface';
import generateAuthHeaders from './generateAuthHeaders';
import { thunkMiddlewareExtraArgument } from './thunkMiddleware';
import { onError } from '@apollo/client/link/error';
import logger from '../services/logger.service';
import { PerfLogTypes, ShellSDK } from '@walkme/client-shell-sdk';

export type ApolloClient = ApolloClientSource<Record<string, unknown>>;

export const useApolloClient = () => {
    const client = useApolloClientSource();

    return client as ApolloClient;
};

const getErrorInfoInGrapqhlError = (error: ApolloError): void | { errorStatusCode?: number; errorMessage?: string } => {
    for (let i = 0; i < error.graphQLErrors.length; i++) {
        const err = error.graphQLErrors[i];
        const errorMessage = err.message;

        const statusCodeFromException = err.extensions?.exception?.status;
        if (typeof statusCodeFromException === 'number') {
            return { errorStatusCode: statusCodeFromException, errorMessage };
        }
        const statusCodeFromResponse = err.extensions?.response?.statusCode;
        if (typeof statusCodeFromResponse === 'number') {
            return { errorStatusCode: statusCodeFromResponse, errorMessage };
        }
    }

    if (error.networkError) {
        return { errorStatusCode: (error.networkError as ServerParseError | ServerError).statusCode, errorMessage: error.networkError.message };
    }

    if (error.message) {
        return { errorMessage: error.message };
    }
};

function configureApolloClient({
    store,
    uri = config.graphQlUrl,
    shellSdk
}: {
    store: Store<IStore>;
    uri?: string;
    shellSdk: ShellSDK;
}) {
    const omitTypenameLink = new ApolloLink((operation, forward) => {
        if (operation.variables) {
            operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
        }

        return forward(operation);
    });

    const errorLink = onError(error => {
        const { operation, ...rest } = error;

        const body = {
            ...operation,
            query: print(operation.query)
        };

        let apolloError;

        try {
            apolloError = new ApolloError(error);
        } catch (err) {
            logger.log('failed to parse apollo error', { error: err, originalError: error, body, ...rest }, 'warn');
            shellSdk.logPerfEvent(PerfLogTypes.API_END, {
              requestUrl: uri,
              pathName: config.shellSdkAppName,
              operationName: operation.operationName
            })
            return logger.error(`graphql error for operation ${body.operationName} (failed to parse to apollo error)`, error, { body, ...rest });
        }

        if (apolloError) {
            const { errorStatusCode, errorMessage } = getErrorInfoInGrapqhlError(apolloError) || {};

            shellSdk.logPerfEvent(PerfLogTypes.API_END, {
              requestUrl: uri,
              pathName: config.shellSdkAppName,
              operationName: operation.operationName,
              status: errorStatusCode,
              error: errorMessage
            })
            if (errorStatusCode) {
                if (errorStatusCode === 502 || errorStatusCode === 401 || errorStatusCode === 403) {
                    return logger.log(
                        `graphql response error (status-code ${errorStatusCode}) for operation ${body.operationName}: ${errorMessage}`,
                        { body, ...rest, errorStatusCode },
                        'warn'
                    );
                }

                return logger.error(
                    `graphql response error (status-code ${errorStatusCode}) for operation ${body.operationName}: ${errorMessage}`,
                    apolloError,
                    {
                        body,
                        ...rest,
                        errorStatusCode
                    }
                );
            }

            return logger.error(`graphql error (no status code) for operation ${body.operationName}: ${errorMessage}`, apolloError, {
                body,
                ...rest
            });
        }
    });

    const defaultTimeoutLink = new ApolloLinkTimeout(config.basicFetchTimeout);

    const wrappedTimeoutLink = new ApolloLink((operation, forward) => {
        return new Observable(observer => {
          let timeoutLink = defaultTimeoutLink;
          const customTimeout = operation.getContext().timeout;
          if (customTimeout) {
            timeoutLink = new ApolloLinkTimeout(customTimeout);
          }
          timeoutLink.request(operation, forward).subscribe({
                next: observer.next.bind(observer),
                error: error => {
                    if (!error?.response) {
                        const body = {
                            ...operation,
                            query: print(operation.query)
                        };

                        logger.error(`graphql timeout error (status-code ${error?.statusCode}) for operation ${operation.operationName}`, error, {
                            body,
                            errorStatusCode: error?.statusCode
                        });
                        shellSdk.logPerfEvent(PerfLogTypes.API_END, {
                          requestUrl: uri,
                          pathName: config.shellSdkAppName,
                          operationName: operation.operationName,
                          status: error?.statusCode,
                          message: error?.message
                        })
                    }

                    return observer.error.bind(observer)(error);
                },
                complete: observer.complete.bind(observer)
            });
        });
    });

    function omitTypename(key: $TSFixMe, value: $TSFixMe) {
        return key === '__typename' ? undefined : value;
    }

    const authLink = setContext(async (_, { headers }) => {
        const authHeaders = await generateAuthHeaders({ store, shellSdk });
        return { headers: { ...headers, ...authHeaders } };
    });

    const httpLinkOptions = { uri };
    if (process.env.NODE_ENV === 'test') {
        Object.assign(httpLinkOptions, { fetch: global.fetch });
    }

    const httpLink = createHttpLink(httpLinkOptions);

    const perfLogLink = new ApolloLink((operation, forward) => {
      shellSdk.logPerfEvent(PerfLogTypes.API_START, {
        requestUrl: uri,
        pathName: config.shellSdkAppName,
        operationName: operation.operationName
      })
      return forward(operation).map((response) => {
        shellSdk.logPerfEvent(PerfLogTypes.API_END, {
          requestUrl: uri,
          pathName: config.shellSdkAppName,
          operationName: operation.operationName,
          status: 200,
        })
        return response
      })
    })

    const client = new ApolloClientSource({
        cache: new InMemoryCache({
            typePolicies: {
                DetailedAnalyticsResult: {
                    keyFields: (detailedAnalyticsResult: Readonly<StoreObject>) => `DetailedAnalyticsResult:${detailedAnalyticsResult.numericId}`
                },
                AggregationsResultsGraphql: {
                    merge: true
                },
                UserSettings: {
                    merge: true
                },
                UserGraphql: {
                    keyFields: (user: Readonly<StoreObject>) => `UserGraphql:${user.id}${user.isPublicApiUser ? ':publicApiUser' : ''}`
                }
            }
        }),
        link: ApolloLink.from([perfLogLink, errorLink, wrappedTimeoutLink, omitTypenameLink, authLink, httpLink]),
        connectToDevTools: true
    });

    thunkMiddlewareExtraArgument.apolloClient = client;

    return client;
}

export default configureApolloClient;
